import React, { useCallback, useEffect, useState } from 'react';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { claimMembershipTrial, fetchMyPlanAccess } from '../../services/membershipPlanService';

const { FiGift, FiLoader, FiCheckCircle } = FiIcons;

const MembershipPlanAccessPanel = () => {
  const [access, setAccess] = useState({ trial_plans: [], active_assignments: [] });
  const [loading, setLoading] = useState(true);
  const [claiming, setClaiming] = useState('');
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const refresh = useCallback(async () => {
    setError('');
    try {
      setAccess(await fetchMyPlanAccess());
    } catch (requestError) {
      setError(requestError.message || 'Unable to load plan access.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const claim = async (plan) => {
    setClaiming(plan.id);
    setError('');
    setMessage('');
    try {
      await claimMembershipTrial(plan.id);
      setMessage(`${plan.name} trial claimed. It expires in ${plan.trial_duration_days} days.`);
      await refresh();
    } catch (requestError) {
      setError(requestError.message || 'Unable to claim this trial.');
    } finally {
      setClaiming('');
    }
  };

  return (
    <section className="mx-auto mt-8 w-full max-w-3xl rounded-[2.5rem] border border-indigo-100 bg-white p-7 shadow-sm md:p-9">
      <div className="flex items-center gap-3">
        <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-600"><SafeIcon icon={FiGift} /></div>
        <div><h2 className="text-xl font-black text-slate-900">Plans and access</h2><p className="text-xs text-slate-500">Review active access or claim an available trial.</p></div>
      </div>

      {error && <p role="alert" className="mt-4 rounded-xl bg-rose-50 p-3 text-sm font-bold text-rose-700">{error}</p>}
      {message && <p className="mt-4 rounded-xl bg-emerald-50 p-3 text-sm font-bold text-emerald-700">{message}</p>}
      {loading ? <SafeIcon icon={FiLoader} className="mt-5 animate-spin text-2xl text-indigo-600" /> : (
        <>
          <div className="mt-5 space-y-3">
            {access.active_assignments.map((assignment) => (
              <div key={assignment.assignment_id} className="rounded-2xl bg-emerald-50 p-4">
                <p className="flex items-center gap-2 font-black text-emerald-800"><SafeIcon icon={FiCheckCircle} /> {assignment.plan_name} active</p>
                <p className="mt-1 text-xs text-emerald-700">Access expires {new Date(assignment.expires_at).toLocaleDateString()}.</p>
                {Object.keys(assignment.features || {}).length > 0 && <ul className="mt-2 text-xs text-emerald-800">{Object.entries(assignment.features).map(([feature, detail]) => <li key={feature}>• <b>{feature}</b>{detail === true ? '' : ` — ${detail}`}</li>)}</ul>}
              </div>
            ))}
            {access.active_assignments.length === 0 && <p className="text-sm text-slate-500">No active plan access.</p>}
          </div>
          <div className="mt-6 space-y-3">
            {access.trial_plans.map((plan) => (
              <div key={plan.id} className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-slate-200 p-4">
                <div><p className="font-black text-slate-900">{plan.name} trial</p><p className="text-xs text-slate-500">{plan.description} · {plan.trial_duration_days} days · repeat claim every {plan.claim_interval_months} months</p></div>
                <button type="button" disabled={!plan.can_claim || !!claiming} onClick={() => claim(plan)} className="rounded-xl bg-indigo-600 px-4 py-3 text-xs font-black uppercase tracking-wider text-white disabled:bg-slate-200 disabled:text-slate-500">
                  {claiming === plan.id ? <SafeIcon icon={FiLoader} className="animate-spin" /> : plan.can_claim ? 'Claim trial' : `Available ${new Date(plan.next_claim_at).toLocaleDateString()}`}
                </button>
              </div>
            ))}
            {access.trial_plans.length === 0 && <p className="text-sm text-slate-500">No trials are available right now.</p>}
          </div>
        </>
      )}
    </section>
  );
};

export default MembershipPlanAccessPanel;
